import { useState, useEffect } from 'react'
import { Link } from 'react-router-dom'
import { Users, Plus, Copy, Trash2, Check, Eye, UserCheck, Briefcase, Users2, AlertTriangle, Info, HelpCircle, BookOpen, ExternalLink } from 'lucide-react'
import { useAuth } from '../contexts/AuthContext'
import { useSupabaseQuery, useSupabaseInsert, useSupabaseUpdate, formatDate } from '../hooks/useSupabase'
import { supabase } from '../lib/supabase'
import { Button, Card, Input, Select, Modal, EmptyState, PageHeader, Badge, Toast } from '../components/ui/SharedUI'

const SHARE_TYPES = [
  {
    value: 'verification',
    label: 'Shift verification',
    icon: UserCheck,
    description: 'They can confirm, dispute, or mark unsure on each shift.',
    fields: ['name', 'email', 'recipient'],
    permissions: { view_shifts: true, view_notes: false, view_pay: false, can_corroborate: true, can_export: false },
  },
  {
    value: 'oversight',
    label: 'Care oversight',
    icon: Eye,
    description: 'They can view shifts, evidence notes, and the calendar.',
    fields: ['name', 'email', 'relationship', 'recipient'],
    permissions: { view_shifts: true, view_notes: true, view_pay: false, can_corroborate: false, can_export: false },
  },
  {
    value: 'professional',
    label: 'Professional review',
    icon: Briefcase,
    description: 'They can view shifts, notes, pay, and export CSV/PDF.',
    fields: ['name', 'email', 'organization', 'title', 'recipient'],
    permissions: { view_shifts: true, view_notes: true, view_pay: true, can_corroborate: false, can_export: true },
  },
  {
    value: 'coordination',
    label: 'Care team coordination',
    icon: Users2,
    description: 'They see your shift schedule (times only, no notes or pay).',
    fields: ['name', 'email', 'recipient'],
    permissions: { view_shifts: true, view_notes: false, view_pay: false, can_corroborate: false, can_export: false, schedule_only: true },
  },
]

export function SharingPage() {
  const { user } = useAuth()
  const { data: shares, loading, refetch } = useSupabaseQuery('shares', { orderBy: 'created_at', ascending: false })
  const { data: recipients } = useSupabaseQuery('recipients', { column: 'owner_id', orderBy: 'name', ascending: true })
  const { insert } = useSupabaseInsert('shares')
  const { update } = useSupabaseUpdate('shares')

  const [modalOpen, setModalOpen] = useState(false)
  const [toast, setToast] = useState(null)
  const [revokeConfirm, setRevokeConfirm] = useState(null)
  const [copiedId, setCopiedId] = useState(null)
  const [corroborationStats, setCorroborationStats] = useState({})

  const emptyForm = {
    share_type: 'verification',
    shared_with_name: '',
    shared_with_email: '',
    shared_with_organization: '',
    shared_with_title: '',
    shared_with_relationship: '',
    recipient_id: '',
    expires_days: '30',
    attested: false,
  }
  const [form, setForm] = useState(emptyForm)

  const recipientMap = Object.fromEntries(recipients.map(r => [r.id, r.name]))

  // Load corroboration stats per share
  useEffect(() => {
    if (!shares.length) return
    async function loadStats() {
      const shareIds = shares.map(s => s.id)
      const { data } = await supabase
        .from('corroborations')
        .select('share_id, state')
        .in('share_id', shareIds)

      const stats = {}
      for (const c of (data || [])) {
        if (!stats[c.share_id]) stats[c.share_id] = { confirmed: 0, disputed: 0, unsure: 0, total: 0 }
        stats[c.share_id][c.state]++
        stats[c.share_id].total++
      }
      setCorroborationStats(stats)
    }
    loadStats()
  }, [shares])

  function getShareUrl(token) {
    return `${window.location.origin}/s/${token}`
  }

  function getStatus(share) {
    if (share.revoked_at) return { label: 'Revoked', variant: 'gray' }
    if (share.expires_at && new Date(share.expires_at) < new Date()) return { label: 'Expired', variant: 'amber' }
    return { label: 'Active', variant: 'green' }
  }

  const activeType = SHARE_TYPES.find(t => t.value === form.share_type) || SHARE_TYPES[0]

  async function handleCreate(e) {
    e.preventDefault()
    if (!form.attested) {
      setToast({ message: 'Please attest to the accuracy of the records before sharing', type: 'error' })
      return
    }
    try {
      const expiresAt = form.expires_days === 'never'
        ? null
        : new Date(Date.now() + parseInt(form.expires_days) * 86400000).toISOString()

      await insert({
        caregiver_id: user.id,
        share_type: form.share_type,
        shared_with_name: form.shared_with_name,
        shared_with_email: form.shared_with_email || null,
        shared_with_organization: activeType.fields.includes('organization') ? (form.shared_with_organization || null) : null,
        shared_with_title: activeType.fields.includes('title') ? (form.shared_with_title || null) : null,
        shared_with_relationship: activeType.fields.includes('relationship') ? (form.shared_with_relationship || null) : null,
        recipient_id: form.recipient_id || null,
        expires_at: expiresAt,
        permissions: activeType.permissions,
      })
      setModalOpen(false)
      setForm(emptyForm)
      setToast({ message: 'Share link created — copy it and send to the recipient', type: 'success' })
      refetch()
    } catch (err) {
      setToast({ message: err.message, type: 'error' })
    }
  }

  async function handleRevoke(share) {
    try {
      await update(share.id, { revoked_at: new Date().toISOString() })
      setRevokeConfirm(null)
      setToast({ message: 'Share access revoked', type: 'success' })
      refetch()
    } catch (err) {
      setToast({ message: err.message, type: 'error' })
    }
  }

  function copyLink(share) {
    navigator.clipboard.writeText(getShareUrl(share.token))
    setCopiedId(share.id)
    setTimeout(() => setCopiedId(null), 2000)
  }

  const activeShares = shares.filter(s => !s.revoked_at && (!s.expires_at || new Date(s.expires_at) > new Date()))
  const inactiveShares = shares.filter(s => s.revoked_at || (s.expires_at && new Date(s.expires_at) <= new Date()))

  if (loading) return <div style={{ padding: '2rem', color: '#718096' }}>Loading...</div>

  return (
    <div style={{ padding: '2rem', maxWidth: '900px' }}>
      <PageHeader
        title="Sharing"
        description="Grant scoped access to family members, case managers, and care team members. You control who sees what and can revoke at any time."
        action={
          <div style={{ display: 'flex', gap: '8px' }}>
            <Link to="/sharing/guide" style={{ textDecoration: 'none' }}>
              <Button variant="secondary"><BookOpen size={16} /> Sharing guide</Button>
            </Link>
            <Button onClick={() => setModalOpen(true)}><Plus size={16} /> Create share link</Button>
          </div>
        }
      />

      {/* Explanation card */}
      <Card style={{ marginBottom: '1.5rem', background: '#F7FAFC', borderLeft: '3px solid #2C7A7B' }}>
        <div style={{ fontSize: '14px', color: '#4A5568', lineHeight: 1.7 }}>
          <strong style={{ color: '#1A202C' }}>How sharing works.</strong> Choose a share type based on who you're sharing with — from simple shift verification to full professional review. You copy the link and send it yourself (auto-notification emails are coming soon). Interactive shares (verification and professional) require email verification before the person can act. <Link to="/sharing/guide" style={{ color: '#2C7A7B', fontWeight: 500 }}>Read the full sharing guide →</Link>
        </div>
      </Card>

      {shares.length === 0 ? (
        <EmptyState
          icon={Users}
          title="No shares yet"
          description="Create a share link to let a family member, case manager, or care team member see your documented shifts — with the right level of access for their role."
          action={<Button onClick={() => setModalOpen(true)}><Plus size={16} /> Create your first share link</Button>}
        />
      ) : (
        <>
          {activeShares.length > 0 && (
            <div style={{ marginBottom: '1.5rem' }}>
              <h2 style={{ fontSize: '14px', fontWeight: 500, color: '#718096', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: '10px' }}>
                Active shares ({activeShares.length})
              </h2>
              <div style={{ display: 'grid', gap: '10px' }}>
                {activeShares.map(share => (
                  <ShareCard
                    key={share.id}
                    share={share}
                    recipientMap={recipientMap}
                    stats={corroborationStats[share.id]}
                    onCopy={() => copyLink(share)}
                    onRevoke={() => setRevokeConfirm(share)}
                    copied={copiedId === share.id}
                    getStatus={getStatus}
                  />
                ))}
              </div>
            </div>
          )}

          {inactiveShares.length > 0 && (
            <div>
              <h2 style={{ fontSize: '14px', fontWeight: 500, color: '#718096', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: '10px' }}>
                Expired and revoked ({inactiveShares.length})
              </h2>
              <div style={{ display: 'grid', gap: '10px' }}>
                {inactiveShares.map(share => (
                  <ShareCard
                    key={share.id}
                    share={share}
                    recipientMap={recipientMap}
                    stats={corroborationStats[share.id]}
                    getStatus={getStatus}
                    inactive
                  />
                ))}
              </div>
            </div>
          )}
        </>
      )}

      {/* Create share modal */}
      <Modal open={modalOpen} onClose={() => setModalOpen(false)} title="Create a share link" width={560}>
        <form onSubmit={handleCreate}>

          {/* Share type selector */}
          <div style={{ marginBottom: '1.25rem' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
              <div style={{ fontSize: '13px', fontWeight: 500, color: '#4A5568' }}>Choose a share type</div>
              <Link
                to="/sharing/guide"
                target="_blank"
                style={{ fontSize: '12px', color: '#2C7A7B', textDecoration: 'none', display: 'inline-flex', alignItems: 'center', gap: '3px', fontWeight: 500 }}
              >
                Compare all types <ExternalLink size={11} />
              </Link>
            </div>
            <div style={{ display: 'grid', gap: '8px' }}>
              {SHARE_TYPES.map(type => {
                const Icon = type.icon
                const selected = form.share_type === type.value
                return (
                  <label key={type.value} style={{
                    display: 'flex', alignItems: 'flex-start', gap: '10px', padding: '12px',
                    border: `1px solid ${selected ? '#2C7A7B' : '#E2E8F0'}`,
                    borderRadius: '8px', cursor: 'pointer',
                    background: selected ? '#E1F5EE' : '#FFFFFF',
                  }}>
                    <input type="radio" name="share_type" value={type.value} checked={selected}
                      onChange={e => setForm({ ...form, share_type: e.target.value })} style={{ marginTop: '2px' }} />
                    <div style={{ flex: 1 }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '2px' }}>
                        <div style={{ fontSize: '14px', fontWeight: 500, color: '#1A202C', display: 'flex', alignItems: 'center', gap: '6px' }}>
                          <Icon size={15} /> {type.label}
                        </div>
                        <Link
                          to={`/sharing/guide#${type.value}`}
                          target="_blank"
                          onClick={e => e.stopPropagation()}
                          style={{ fontSize: '11px', color: '#2C7A7B', textDecoration: 'none', fontWeight: 500 }}
                        >
                          See details →
                        </Link>
                      </div>
                      <div style={{ fontSize: '13px', color: '#718096', lineHeight: 1.5 }}>
                        {type.description}
                      </div>
                    </div>
                  </label>
                )
              })}
            </div>
          </div>

          {/* Full name */}
          <div style={{ marginBottom: '1rem' }}>
            <label style={{ display: 'block', fontSize: '13px', fontWeight: 500, color: '#4A5568', marginBottom: '6px' }}>
              Full name <span style={{ color: '#993C1D', fontWeight: 400 }}>· required</span>
            </label>
            <input
              type="text"
              value={form.shared_with_name}
              onChange={e => setForm({ ...form, shared_with_name: e.target.value })}
              required
              placeholder={
                form.share_type === 'professional' ? 'e.g. Annette Barnes' :
                form.share_type === 'oversight' ? 'e.g. Sarah Holloway' :
                form.share_type === 'coordination' ? 'e.g. Suzanne Smith' :
                'e.g. Sarah Holloway'
              }
              style={inputStyle}
            />
          </div>

          {/* Email address */}
          <div style={{ marginBottom: '1rem' }}>
            <label style={{ display: 'block', fontSize: '13px', fontWeight: 500, color: '#4A5568', marginBottom: '6px' }}>
              Email address
              {activeType.permissions.can_corroborate || activeType.permissions.can_export ? (
                <span style={{ color: '#993C1D', fontWeight: 400 }}> · required</span>
              ) : (
                <span style={{ color: '#718096', fontWeight: 400 }}> · optional</span>
              )}
            </label>
            <input
              type="email"
              value={form.shared_with_email}
              onChange={e => setForm({ ...form, shared_with_email: e.target.value })}
              required={activeType.permissions.can_corroborate || activeType.permissions.can_export}
              placeholder={form.share_type === 'professional' ? 'e.g. annette@360cares.org' : 'e.g. name@email.com'}
              style={inputStyle}
            />
            <p style={{ fontSize: '12px', color: '#718096', marginTop: '4px', lineHeight: 1.5, display: 'flex', alignItems: 'flex-start', gap: '4px' }}>
              <Info size={11} style={{ flexShrink: 0, marginTop: '2px' }} />
              {activeType.permissions.can_corroborate || activeType.permissions.can_export ? (
                <span>They'll verify this email before they can {activeType.permissions.can_corroborate ? 'confirm or dispute shifts' : 'export records'}. Auto-notification emails coming soon.</span>
              ) : (
                <span>Auto-notification emails are coming soon. For now, copy the link and send it yourself.</span>
              )}
            </p>
          </div>

          {/* Relationship (oversight only) */}
          {activeType.fields.includes('relationship') && (
            <div style={{ marginBottom: '1rem' }}>
              <label style={{ display: 'block', fontSize: '13px', fontWeight: 500, color: '#4A5568', marginBottom: '6px' }}>
                Relationship to care recipient <span style={{ color: '#718096', fontWeight: 400 }}>· optional</span>
              </label>
              <input
                type="text"
                value={form.shared_with_relationship}
                onChange={e => setForm({ ...form, shared_with_relationship: e.target.value })}
                placeholder="e.g. Sister, Son, Grandchild"
                style={inputStyle}
              />
            </div>
          )}

          {/* Organization (professional only) */}
          {activeType.fields.includes('organization') && (
            <div style={{ marginBottom: '1rem' }}>
              <label style={{ display: 'block', fontSize: '13px', fontWeight: 500, color: '#4A5568', marginBottom: '6px' }}>
                Organization <span style={{ color: '#718096', fontWeight: 400 }}>· optional</span>
              </label>
              <input
                type="text"
                value={form.shared_with_organization}
                onChange={e => setForm({ ...form, shared_with_organization: e.target.value })}
                placeholder="e.g. 360 Cares, Georgia Legal Services, Georgia DOL"
                style={inputStyle}
              />
            </div>
          )}

          {/* Role or title (professional only) */}
          {activeType.fields.includes('title') && (
            <div style={{ marginBottom: '1rem' }}>
              <label style={{ display: 'block', fontSize: '13px', fontWeight: 500, color: '#4A5568', marginBottom: '6px' }}>
                Role or title <span style={{ color: '#718096', fontWeight: 400 }}>· optional</span>
              </label>
              <input
                type="text"
                value={form.shared_with_title}
                onChange={e => setForm({ ...form, shared_with_title: e.target.value })}
                placeholder="e.g. Case Manager, Attorney, Investigator"
                style={inputStyle}
              />
            </div>
          )}

          {/* Care recipient scope */}
          {recipients.length > 0 && (
            <div style={{ marginBottom: '1rem' }}>
              <label style={{ display: 'block', fontSize: '13px', fontWeight: 500, color: '#4A5568', marginBottom: '6px' }}>
                Share shifts for which care recipient?
              </label>
              <select
                value={form.recipient_id}
                onChange={e => setForm({ ...form, recipient_id: e.target.value })}
                style={{ ...inputStyle, appearance: 'none' }}
              >
                <option value="">All care recipients</option>
                {recipients.map(r => <option key={r.id} value={r.id}>{r.name}</option>)}
              </select>
            </div>
          )}

          {/* Expiration */}
          <div style={{ marginBottom: '1rem' }}>
            <label style={{ display: 'block', fontSize: '13px', fontWeight: 500, color: '#4A5568', marginBottom: '6px' }}>
              Expires after
            </label>
            <select
              value={form.expires_days}
              onChange={e => setForm({ ...form, expires_days: e.target.value })}
              style={{ ...inputStyle, appearance: 'none' }}
            >
              <option value="7">7 days</option>
              <option value="30">30 days</option>
              <option value="90">90 days</option>
              <option value="365">1 year</option>
              <option value="never">Never expires</option>
            </select>
          </div>

          {/* Attestation */}
          <div style={{
            padding: '12px 14px',
            background: '#F7FAFC',
            border: `1px solid ${form.attested ? '#2C7A7B' : '#E2E8F0'}`,
            borderRadius: '8px',
            marginTop: '1rem',
          }}>
            <label style={{ display: 'flex', gap: '10px', alignItems: 'flex-start', cursor: 'pointer' }}>
              <input
                type="checkbox"
                checked={form.attested}
                onChange={e => setForm({ ...form, attested: e.target.checked })}
                style={{ marginTop: '3px', flexShrink: 0 }}
              />
              <span style={{ fontSize: '13px', color: '#4A5568', lineHeight: 1.6 }}>
                <strong style={{ color: '#1A202C' }}>Attestation.</strong> I attest that the records I'm sharing are accurate to the best of my knowledge, that the care recipient named in these records is a real person for whom I provide or oversee care, and that any observed shifts are documented in good faith based on personal observation or trusted reports.
              </span>
            </label>
          </div>

          <div style={{ display: 'flex', gap: '8px', justifyContent: 'flex-end', marginTop: '1rem' }}>
            <Button variant="secondary" onClick={() => setModalOpen(false)}>Cancel</Button>
            <Button type="submit" disabled={!form.attested}>Create share link</Button>
          </div>
        </form>
      </Modal>

      {/* Revoke confirmation */}
      <Modal open={!!revokeConfirm} onClose={() => setRevokeConfirm(null)} title="Revoke share access?" width={400}>
        <p style={{ color: '#4A5568', marginBottom: '1rem', lineHeight: 1.6 }}>
          This will immediately revoke <strong>{revokeConfirm?.shared_with_name}</strong>'s access. Any corroborations they've already submitted will remain on your record.
        </p>
        <div style={{ display: 'flex', gap: '8px', justifyContent: 'flex-end' }}>
          <Button variant="secondary" onClick={() => setRevokeConfirm(null)}>Cancel</Button>
          <Button variant="danger" onClick={() => handleRevoke(revokeConfirm)}>Revoke access</Button>
        </div>
      </Modal>

      {toast && <Toast message={toast.message} type={toast.type} onClose={() => setToast(null)} />}
    </div>
  )
}

function ShareCard({ share, recipientMap, stats, onCopy, onRevoke, copied, getStatus, inactive }) {
  const status = getStatus(share)
  const recipientName = share.recipient_id ? recipientMap[share.recipient_id] : 'All care recipients'
  const typeConfig = SHARE_TYPES.find(t => t.value === share.share_type) || SHARE_TYPES[0]
  const TypeIcon = typeConfig.icon

  return (
    <Card style={{ opacity: inactive ? 0.6 : 1 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: '12px', marginBottom: stats?.total > 0 || share.last_accessed_at ? '10px' : 0 }}>
        <div style={{ flex: 1 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '6px', flexWrap: 'wrap' }}>
            <span style={{ fontSize: '15px', fontWeight: 500, color: '#1A202C' }}>
              {share.shared_with_name}
            </span>
            <Badge variant={status.variant}>{status.label}</Badge>
            <Badge variant="default">
              <TypeIcon size={11} style={{ verticalAlign: 'middle', marginRight: '2px' }} />
              {typeConfig.label}
            </Badge>
          </div>
          <div style={{ fontSize: '13px', color: '#718096', lineHeight: 1.6 }}>
            {share.shared_with_email && <div>{share.shared_with_email}</div>}
            {share.shared_with_organization && (
              <div>
                {share.shared_with_title ? `${share.shared_with_title} · ` : ''}
                {share.shared_with_organization}
              </div>
            )}
            {share.shared_with_relationship && <div>Relationship: {share.shared_with_relationship}</div>}
            <div style={{ marginTop: '2px' }}>
              Shifts for: {recipientName} · Created {formatDate(share.created_at)}
              {share.expires_at && ` · Expires ${formatDate(share.expires_at)}`}
            </div>
          </div>
        </div>
        <div style={{ display: 'flex', gap: '4px' }}>
          {!inactive && (
            <>
              <Button variant="secondary" size="sm" onClick={onCopy}>
                {copied ? <><Check size={14} /> Copied</> : <><Copy size={14} /> Copy link</>}
              </Button>
              <Button variant="ghost" size="sm" onClick={onRevoke}>
                <Trash2 size={14} />
              </Button>
            </>
          )}
        </div>
      </div>

      {(stats?.total > 0 || share.last_accessed_at) && (
        <div style={{ paddingTop: '10px', borderTop: '1px solid #EDF2F7', display: 'flex', gap: '16px', flexWrap: 'wrap', alignItems: 'center' }}>
          {stats?.total > 0 && (
            <>
              {stats.confirmed > 0 && (
                <span style={{ display: 'flex', alignItems: 'center', gap: '4px', fontSize: '13px', color: '#0F6E56' }}>
                  <Check size={13} /> <strong>{stats.confirmed}</strong> confirmed
                </span>
              )}
              {stats.disputed > 0 && (
                <span style={{ display: 'flex', alignItems: 'center', gap: '4px', fontSize: '13px', color: '#993C1D' }}>
                  <AlertTriangle size={13} /> <strong>{stats.disputed}</strong> disputed
                </span>
              )}
              {stats.unsure > 0 && (
                <span style={{ display: 'flex', alignItems: 'center', gap: '4px', fontSize: '13px', color: '#8b5000' }}>
                  <HelpCircle size={13} /> <strong>{stats.unsure}</strong> unsure
                </span>
              )}
            </>
          )}
          {share.last_accessed_at && (
            <span style={{ fontSize: '12px', color: '#A0AEC0', marginLeft: 'auto' }}>
              Last opened {formatDate(share.last_accessed_at)}
            </span>
          )}
        </div>
      )}
    </Card>
  )
}

const inputStyle = {
  width: '100%',
  padding: '9px 12px',
  border: '1px solid #E2E8F0',
  borderRadius: '6px',
  fontSize: '14px',
  background: '#FFFFFF',
}
